import { lazy } from 'react';

import { Navigate } from 'react-router';

import type { AppRoute } from '@enterprise/types';

import { RouteGuard } from '@/app/components/route-guard/route-guard';
import { bluePrintDetailLoader } from '@/app/pages/loaders/blue-print-detail.loader';
import { defaultSavedFilterLoader } from '@/app/pages/loaders/default-saved-filter.loader';
import { templateLoader } from '@/app/pages/loaders/template-loader';
import MergePurgeReport from '@feat/reports/merge-purge-report';

const AppendPage = lazy(() => {
  return import('@/app/pages/appends');
});
const BlueprintDetail = lazy(() => {
  return import('@/app/pages/blueprint-detail');
});
const BlueprintManager = lazy(() => {
  return import('@/app/pages/blueprint-manager');
});
const CalendarPage = lazy(() => {
  return import('@/app/pages/calendar');
});
const ListManager = lazy(() => {
  return import('@/app/pages/list-manager');
});
const MergePurge = lazy(() => {
  return import('@/app/pages/merge-purge');
});
const MissingLists = lazy(() => {
  return import('@/app/pages/missing-lists');
});
const PackageManager = lazy(() => {
  return import('@/app/pages/package-manager');
});
const ProfilePage = lazy(() => {
  return import('@/app/pages/profile');
});
const RecipientManager = lazy(() => {
  return import('@/app/pages/recipient-manager');
});
const Recipients = lazy(() => {
  return import('@/components/listManager/recipients');
});
const SeedsPage = lazy(() => {
  return import('@/app/pages/seeds');
});
const SuppressionsPage = lazy(() => {
  return import('@/app/pages/suppressions');
});
const SuppressionDetailsPage = lazy(() => {
  return import('@/app/pages/suppression-details');
});
const SuppressionRecipientsPage = lazy(() => {
  return import('@/app/pages/suppression-recipients');
});
const UserManagementPage = lazy(() => {
  return import('@/app/pages/user-management');
});

export const datastreamRoutes: AppRoute = {
  children: [
    {
      element: (
        <Navigate
          replace
          to="packagemanager"
        />
      ),
      index: true,
    },
    // Administration
    {
      breadcrumb: { label: 'Administration' },
      children: [
        {
          element: (
            <Navigate
              replace
              to="suppressions"
            />
          ),
          index: true,
        },
        // Admin - Appends
        {
          breadcrumb: { label: 'Appends' },
          children: [
            {
              element: <AppendPage />,
              index: true,
            },
          ],
          element: <RouteGuard />,
          path: 'appends',
        },
        // Admin - Seeds
        {
          breadcrumb: { label: 'Seeds' },
          children: [
            {
              element: <SeedsPage />,
              index: true,
              meta: {
                type: 'seed',
              },
            },
            {
              breadcrumb: {
                propertyLocation: 'param',
                propertyName: 'suppressionName',
              },
              children: [
                {
                  element: (
                    <Navigate
                      replace
                      to="overview"
                    />
                  ),
                  index: true,
                },
                // Suppression Name - Overview
                {
                  element: <SuppressionDetailsPage />,
                  loader: templateLoader,
                  path: 'overview',
                },
                // Suppression Name - Recipients
                {
                  element: <SuppressionRecipientsPage />,
                  loader: templateLoader,
                  path: 'recipients',
                },
              ],
              element: <RouteGuard />,
              path: ':suppressionName',
            },
          ],
          element: <RouteGuard />,
          path: 'seeds',
        },
        // Admin - Merge/Purge
        {
          breadcrumb: {
            canNavigate: false,
            label: 'Merge Purge',
          },
          children: [
            {
              children: [
                {
                  breadcrumb: {
                    label: 'Report',
                    propertyLocation: 'searchParams',
                    propertyName: 'mailing',
                  },
                  element: <MergePurgeReport />,
                  path: ':mailingId/:view',
                },
              ],
              element: <MergePurge />,
            },
          ],
          element: <RouteGuard />,
          path: 'mergepurge',
        },
        // Admin - Suppressions
        {
          breadcrumb: { label: 'Suppressions' },
          children: [
            {
              element: <SuppressionsPage />,
              index: true,
              meta: {
                type: 'suppression',
              },
            },
            // Suppression Name
            {
              breadcrumb: {
                propertyLocation: 'param',
                propertyName: 'suppressionName',
              },
              children: [
                {
                  element: (
                    <Navigate
                      replace
                      to="overview"
                    />
                  ),
                  index: true,
                },
                // Suppression Name - Overview
                {
                  element: <SuppressionDetailsPage />,
                  loader: templateLoader,
                  path: 'overview',
                },
                // Suppression Name - Recipients
                {
                  element: <SuppressionRecipientsPage />,
                  loader: templateLoader,
                  path: 'recipients',
                },
              ],
              element: <RouteGuard />,
              path: ':suppressionName',
            },
          ],
          element: <RouteGuard />,
          path: 'suppressions',
        },
        // Admin - User Management
        {
          breadcrumb: { label: 'User Management' },
          children: [
            {
              element: <UserManagementPage />,
              index: true,
            },
          ],
          element: <RouteGuard permissions={['view-users']} />,
          path: 'usermanagement',
        },
      ],
      element: <RouteGuard />,
      path: 'admin',
    },
    // Blueprint Manager
    {
      breadcrumb: { label: 'Blueprint Manager' },
      children: [
        {
          element: <BlueprintManager />,
          index: true,
        },
        {
          breadcrumb: {
            label: 'Configure Blueprint',
            propertyLocation: 'data',
            propertyName: 'data.niceName',
          },
          element: <BlueprintDetail />,
          loader: bluePrintDetailLoader,
          path: ':blueprintPath',
        },
      ],
      element: <RouteGuard permissions={['dts-view-blueprints']} />,
      path: 'blueprintmanager',
    },
    // Calendar
    {
      breadcrumb: { label: 'Calendar' },
      children: [
        {
          children: [
            {
              breadcrumb: { label: 'External Calendar' },
              children: [
                {
                  element: <CalendarPage />,
                  meta: {
                    type: 'external',
                  },
                  path: '',
                },
              ],
              element: <RouteGuard permissions={['dts-view-mailings-external']} />,
              meta: {
                type: 'external',
              },
              path: 'external',
            },
          ],
          element: <CalendarPage />,
          path: '',
        },
      ],
      element: <RouteGuard />,
      path: 'calendar',
    },
    // List Manager
    {
      breadcrumb: { label: 'List Manager' },
      children: [
        {
          breadcrumb: { label: 'External Mailing Lists' },
          children: [
            {
              children: [
                {
                  element: <Recipients />,
                  path: ':mailingListId/recipients',
                },
              ],
              element: <ListManager />,
              meta: {
                type: 'external',
              },
              path: '',
            },
          ],
          element: <RouteGuard permissions={['dts-view-mailings-external']} />,
          meta: {
            type: 'external',
          },
          path: 'external',
        },
        {
          children: [
            {
              element: <Recipients />,
              path: ':mailingListId/recipients',
            },
          ],
          element: <ListManager />,
          path: '',
        },
        {
          breadcrumb: { label: 'Missing Lists' },
          element: <MissingLists />,
          path: 'missing-lists',
        },
      ],
      element: <RouteGuard />,
      loader: defaultSavedFilterLoader('listmanager'),
      path: 'listmanager',
    },
    // Package Manager
    {
      breadcrumb: { label: 'Package Manager' },
      children: [
        {
          breadcrumb: { label: 'External Mailings' },
          children: [
            {
              element: <PackageManager />,
              index: true,
              meta: {
                type: 'external',
              },
            },
            {
              breadcrumb: { label: 'Closed' },
              element: <PackageManager />,
              meta: {
                isClosed: true,
                type: 'external',
              },
              path: 'closed',
            },
          ],
          element: <RouteGuard permissions={['dts-view-mailings-external']} />,
          meta: {
            type: 'external',
          },
          path: 'external',
        },
        {
          breadcrumb: { label: 'Closed' },
          element: <PackageManager />,
          meta: {
            isClosed: true,
          },
          path: 'closed',
        },
        {
          element: <PackageManager />,
          index: true,
        },
      ],
      element: <RouteGuard />,
      loader: defaultSavedFilterLoader('packagemanager'),
      path: 'packagemanager',
    },
    // Profile
    {
      breadcrumb: { label: 'My Profile' },
      children: [
        {
          element: <ProfilePage />,
          index: true,
        },
      ],
      element: <RouteGuard />,
      path: 'profile',
    },
    // Recipient Manager
    {
      breadcrumb: { label: 'Recipient Manager' },
      children: [
        {
          element: <RecipientManager />,
          index: true,
        },
      ],
      element: <RouteGuard />,
      path: 'recipientmanager',
    },
  ],
  element: <RouteGuard />,
  path: 'datastream',
};
